import {
  faCalendarAlt,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useLanguage } from "../../lib/LanguageContext/LanguageContext.tsx";
import { useLocalizedContent } from "../../hooks/useLocalizedContent.ts";
import { Job } from "../../lib/Data/types.ts";
import { useIntersectionObserver } from "../../hooks";
import CompanyIcon from "./CompanyIcon.tsx";
import styles from "./Experience.module.scss";

interface ExperienceProps {
  jobs: Job[];
}

const Experience: React.FC<ExperienceProps> = ({ jobs }) => {
  const { t, currentLang } = useLanguage();
  const { getLocalizedValue, getLocalizedDate } = useLocalizedContent();
  const sectionRef = useIntersectionObserver();

  const parseMonthYear = (str: string): Date => {
    // "August 2024" -> "August 1, 2024"
    const [month, year] = str.trim().split(" ");
    return new Date(`${month} 1, ${year}`);
  };

  const calculateDuration = (dateRange: string): string => {
    const parts = dateRange.split(" - ");
    const start = parseMonthYear(parts[0]);
    const end =
      parts[1] === "Present" ? new Date() : parseMonthYear(parts[1]);

    const totalMonths =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth());
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    if (currentLang === "zh") {
      if (years > 0 && months > 0) return ` · ${years}年${months}個月`;
      if (years > 0) return ` · ${years}年`;
      return ` · ${months}個月`;
    } else {
      if (years > 0 && months > 0) return ` · ${years} yr ${months} months`;
      if (years > 0) return ` · ${years} yr`;
      return ` · ${months} months`;
    }
  };

  const renderCompanyName = (displayName: string, canonicalName: string) => {
    const urls: Record<string, string> = {
      "Micron Technology": "https://www.micron.com/",
      "CATME Smarter Teamwork": "https://info.catme.org/",
    };
    const url = urls[canonicalName];
    if (!url) return displayName;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.companyLink}
        aria-label={`Visit ${displayName} website (opens in new tab)`}
      >
        {displayName}
      </a>
    );
  };

  // Group jobs by canonical (English) company name
  const groupedJobs = jobs.reduce(
    (acc, job) => {
      const key = job.company;
      if (!acc[key]) {
        acc[key] = {
          canonicalCompany: job.company,
          displayCompany: getLocalizedValue(job, "company"),
          location: getLocalizedValue(job, "location"),
          positions: [],
        };
      }
      acc[key].positions.push({
        title: getLocalizedValue(job, "title"),
        date: job.date,
        displayDate: getLocalizedDate(job.date, job.dateZh),
        responsibilities:
          currentLang === "zh" && job.responsibilitiesZh
            ? job.responsibilitiesZh
            : job.responsibilities,
      });
      return acc;
    },
    {} as Record<
      string,
      {
        canonicalCompany: string;
        displayCompany: string;
        location: string;
        positions: {
          title: string;
          date: string;
          displayDate: string;
          responsibilities: string[];
        }[];
      }
    >
  );

  return (
    <section ref={sectionRef} className={`${styles.experience} experience`}>
      <h2 className="fade-up-section">{t("experience")}</h2>
      <div>
        {Object.values(groupedJobs).map((companyGroup, index) => (
          <div key={index} className="education-item fade-up-section">
            <div className="education-header">
              <div className="name-location">
                <div className={styles.companyNameWithIcon}>
                  <CompanyIcon
                    companyName={companyGroup.canonicalCompany}
                    size={32}
                    className={styles.companyIcon}
                  />
                  <h3>
                    {renderCompanyName(
                      companyGroup.displayCompany,
                      companyGroup.canonicalCompany
                    )}
                  </h3>
                </div>
                <div className="education-location">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    style={{ color: "#dc2626" }}
                    aria-hidden="true"
                  />
                  <span>{companyGroup.location}</span>
                </div>
              </div>
            </div>
            {companyGroup.positions.map((position, posIndex) => (
              <div key={posIndex} className="education-position">
                <p className="education-title">{position.title}</p>
                <p className="education-date">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="calendar-icon"
                    aria-hidden="true"
                  />
                  <span>{position.displayDate}</span>
                  <span className="duration" data-lang={currentLang}>
                    {calculateDuration(position.date)}
                  </span>
                </p>
                <ul className="job-details">
                  {position.responsibilities.map((resp, respIndex) => (
                    <li key={respIndex}>{resp}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

export default Experience;
